import { types, flow, cast } from "mobx-state-tree"
import { saleOrderBusiness } from "businesses/sale-order-business"
import format from "date-fns/format"
import { rootStore } from "stores/root-store"

const SaleOrderStore = types
  .model({
    data: types.optional(types.frozen(), []),
    deliveryInfo: types.maybeNull(types.frozen()),
    isLoading: types.maybeNull(types.boolean),
    customFilters: types.maybeNull(types.frozen()),
    selectedId: types.maybeNull(types.string, ""),
    searchText: types.maybeNull(types.string, ""),
    selectedErpOrder: types.maybeNull(types.string, ""),
    forceReload: types.maybeNull(types.boolean, false),
  })
  .views(self => ({
    get filters() {
      const res = {}
      if (self.customFilters) {
        const dateItems = ["before_date", "after_date"]
        for (const prop in self.customFilters) {
          const val = self.customFilters[prop]
          if (val) {
            if (dateItems.includes(prop)) {
              res[prop] = format(val, "yyyy-MM-dd")
            } else if (Array.isArray(val)) {
              if (val.length > 0) res[prop] = val.join(",")
            } else {
              res[prop] = val
            }
          }
        }
      }

      return res
    },
    get isArchivalSearch() {
      return self.customFilters?.archival_search || false
    },
  }))
  .actions(self => ({
    setForceReload() {
      self.forceReload = !self.forceReload
    },
    setData(data) {
      self.data = data
    },
    setSelectedId(id) {
      self.selectedId = id
    },
    setSearchText(val) {
      self.searchText = val
    },
    setCustomFilters(data) {
      self.customFilters = cast(data)
    },
    setDeliveryInfo(data) {
      self.deliveryInfo = cast(data)
    },
  }))
  .actions(self => ({
    resetData() {
      self.deliveryInfo = null
      self.selectedErpOrder = ""
      self.selectedId = ""
    },
    loadData: flow(function* (showLoading = true) {
      if (!self.searchText) {
        self.setData([])
        return
      }

      if (showLoading) self.isLoading = true
      const searchParams = {
        ...(self.filters || {}),
      }
      const data = yield saleOrderBusiness.getData(searchParams)
      self.setData(data)
      if (showLoading) self.isLoading = false
    }),
    loadDetailData: flow(function* (erpOrder, id) {
      self.selectedId = id
      self.selectedErpOrder = erpOrder
      if (erpOrder == "null") erpOrder = ""

      self.isLoading = true
      const searchParams = {
        erp_order: decodeURIComponent(erpOrder),
        id,
        archival_search: self.isArchivalSearch,
      }
      const deliveryInfo = yield saleOrderBusiness.getDeliveryInformation(searchParams)
      if (deliveryInfo) {
        deliveryInfo.products = deliveryInfo.details || []
      }

      self.setDeliveryInfo(deliveryInfo)

      self.isLoading = false
    }),
    addOrUpdateSaleOrder: flow(function* (data) {
      const result = self.selectedId ? yield saleOrderBusiness.updateData(data) : yield saleOrderBusiness.addData(data)
      return result
    }),
    deleteSaleOrder: flow(function* () {
      self.isLoading = true
      const params = {
        erp_order: self.selectedErpOrder,
        id: self.selectedId,
      }
      const result = yield saleOrderBusiness.deleteData(params)
      self.isLoading = false

      // The API answers with a list: [{ STATUS_CODE: "2040" (orders) / "2041" (receipts), STATUS_NAME: "DELETION_SUCCESS", ... }]
      const status = Array.isArray(result) ? result[0] : result
      return status?.STATUS_NAME === "DELETION_SUCCESS"
    }),
    uploadData: flow(function* (data) {
      const filterService = rootStore.getOptionsByFilter("SERVICE")
      data.forEach(x => {
        if (x.carrier_service) {
          const filterCarrier = filterService.find(item => item.value === x.carrier_service)
          if (filterCarrier?.identifier) x.carrier = filterCarrier.identifier.replace("FILTER_CARRIER_", "").replace("_" + x.carrier_service, "") || ""
        }
      })

      return yield saleOrderBusiness.uploadData(data)
    }),
    batchDelete: flow(function* (selectedIDs) {
      self.isLoading = true
      const erpOrders = self.data.filter(x => selectedIDs.includes(x.ID)).map(item => item.ERP_ORDER)
      const params = {
        erp_order: erpOrders.join(","),
      }
      const result = yield saleOrderBusiness.batchDelete(params)
      self.isLoading = false

      return result
    }),
    setOnOrOffHold: flow(function* (setOnHold) {
      const params = {
        id: self.selectedId,
        set_on_hold: setOnHold,
      }
      const result = yield saleOrderBusiness.setOnOrOffHold(params)

      return result?.length > 0 && !result.some(item => item.StatusCode != 200)
    }),
  }))
export default SaleOrderStore
